import { Component, Inject, inject, OnInit, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ACTIVE_FIELD, COURSE_FIELD, DATE_FIELD, EU_DATE_FIELD, FORTNIGHT_FIELD, MAIN_DATA_STEP, MONTH_FIELD, RECIPIENTS_FIELD, RECIPIENTS_STEP, STRUCTURE_FIELD } from '../../../model/contants/udeu-transfer';
import { StepTypes } from '../../../model/enums/step-types';
import { UDeuStep } from '../../../model/classes/udeu-step';
import { MatFormFieldType } from '../../../model/enums/mat-form-field-types';
import { UdeuService } from '../../../services/udeu.service';
import { ACTIVO_KEY, COURSE_KEY, MESES_DATALIST_NAME, QUINCENA_DATALIST_NAME, SMTDL_OPTION_ITEM_NODE_TYPE, SUBDIVISION_NOMINAL_GAUDE_DATALIST_NAME } from '../../../model/contants/datalists';
import { Store } from '@ngrx/store';
import { AppStore, SnackbarErrorAction } from '@alfresco/aca-shared/store';
import { SMTDataListsService } from 'projects/alden-extension/src/lib/services/data-lists.service';
import { UdeuData, UdeuRecipient, UdeuTransferCalendar } from '../../../model/classes/udeu-transfer-calendar';
import { KeyValue } from '@angular/common';
import { ArrayValidators } from 'projects/alden-extension/src/lib/validators/array.validator';
import { Router } from '@angular/router';
import { ReloadCustomListTableAction } from 'projects/alden-extension/src/lib/store/actions/alden.actions';
import { TransferService } from '../../../services/transfer.service';
import { TransferActionTypes } from '../../../store/actions/transfer-actions';
import { SMTConfirmDialogComponent } from 'projects/alden-extension/src/lib/dialogs/confirm/confirm.dialog';
import { MatStep } from '@angular/material/stepper';
import { SMTLoadingDialogComponent } from 'projects/alden-extension/src/lib/dialogs/loading/loading.dialog';

export interface DialogData {
  transferActionType: TransferActionTypes;
  UUID: string;
}

@Component({
  templateUrl: './create-transfer-calendar.dialog.html',
  styleUrls: ['./create-transfer-calendar.dialog.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class CreateTransferCalendarDialogComponent implements OnInit {
  loading: boolean;

  private _formBuilder = inject(FormBuilder);
  steps: { [id: string]: UDeuStep };

  mainFormGroup: FormGroup<any>;
  dataFormGroup: FormGroup<any>;
  recipientsFormGroup: FormGroup<any>;

  StepTypes: typeof StepTypes = StepTypes;
  MatFormFieldType: typeof MatFormFieldType = MatFormFieldType;
  TransferActionTypes: typeof TransferActionTypes = TransferActionTypes;

  udeuTransferCalendar: UdeuTransferCalendar;

  constructor(@Inject(MAT_DIALOG_DATA) public data: DialogData, private dialogRef: MatDialogRef<CreateTransferCalendarDialogComponent>, private dialog: MatDialog, 
    public udeuService: UdeuService, private transferService: TransferService,
    private smtDataListsService: SMTDataListsService, protected store: Store<AppStore>, private router: Router) {
  }

  async ngOnInit(): Promise<void> {
    this.loading = true;
    this.beforeClosed();
    
    await this.initilizeForms();
    this.createSteps();

    const promises = this.data?.transferActionType !== TransferActionTypes.ViewCalendarTransfer ? [
      // MAIN_DATA_STEP
      this.udeuService.getGaudeList(this.steps, MAIN_DATA_STEP, STRUCTURE_FIELD, SUBDIVISION_NOMINAL_GAUDE_DATALIST_NAME),
      this.udeuService.getMasterList(this.steps, MAIN_DATA_STEP, MONTH_FIELD, MESES_DATALIST_NAME, true),
      this.udeuService.getMasterList(this.steps, MAIN_DATA_STEP, FORTNIGHT_FIELD, QUINCENA_DATALIST_NAME, true),
    ] : [];

    await Promise.all(promises).catch((err) => { 
      console.error("ERROR", err);
      this.store.dispatch(new SnackbarErrorAction('ALDEN.MESSAGES.ERRORS.RESPONSE.500'));
      this.dialogRef.close();
    });
              
    this.loading = false;
  }

  beforeClosed() {
    // Interceptamos el evento de cerrar el diálogo
    this.dialogRef.backdropClick().subscribe(() => {
      this.closeDialog();
    });

    // Interceptamos el evento de presionar la tecla Esc
    this.dialogRef.keydownEvents().subscribe(event => {
      if (event.key === "Escape") {
        this.closeDialog();
      }
    });
  }

  closeDialog() {
    if(this.data?.transferActionType === TransferActionTypes.ViewCalendarTransfer) {
      this.dialogRef.close();
      return;
    }

    const dialogRef = this.dialog.open(SMTConfirmDialogComponent, {
      data: {
        title: 'DEUSTO.NEW_MENU.MENU_ITEMS.TRANSFERS.ACTIONS.CLOSE.DIALOG.TITLE',
        message: 'DEUSTO.NEW_MENU.MENU_ITEMS.TRANSFERS.ACTIONS.CLOSE.DIALOG.MESSAGE',
        noLabel: 'DEUSTO.NEW_MENU.MENU_ITEMS.TRANSFERS.ACTIONS.CLOSE.DIALOG.NO_LABEL',
        yesLabel: 'DEUSTO.NEW_MENU.MENU_ITEMS.TRANSFERS.ACTIONS.CLOSE.DIALOG.YES_LABEL'
      },
      minWidth: '250px'
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {       
        this.dialogRef.close();
      }
    });
  }

  async initilizeForms() {
    const UUID = this.data?.UUID;
    if(UUID) {
      await this.transferService.getTransferCalendar(UUID).then((res) => { 
        this.udeuTransferCalendar = res;
      });
    } else {
      this.udeuTransferCalendar = new UdeuTransferCalendar();
      const promises = [
        this.smtDataListsService.getValueOfKey(COURSE_KEY, SMTDL_OPTION_ITEM_NODE_TYPE).then((res) => { 
          this.udeuTransferCalendar.data.course = res[0]?.value;
        }),
        this.smtDataListsService.getValueOfKey(ACTIVO_KEY, SMTDL_OPTION_ITEM_NODE_TYPE).then((res) => { 
          this.udeuTransferCalendar.data.active = Boolean(res[0]?.value);
        }),
      ];

      await Promise.all(promises).catch((err) => { 
        console.error("ERROR", err);
        this.store.dispatch(new SnackbarErrorAction('ALDEN.MESSAGES.ERRORS.RESPONSE.500'));
        this.dialogRef.close();
      });
    }

    this.dataFormGroup = this._formBuilder.group({
      [STRUCTURE_FIELD]: [this.udeuTransferCalendar?.data?.structure, [Validators.required]],
      [COURSE_FIELD]: [this.udeuTransferCalendar?.data?.course, [Validators.required]],
      [MONTH_FIELD]: [this.udeuTransferCalendar?.data?.month, [Validators.required]],
      [FORTNIGHT_FIELD]: [this.udeuTransferCalendar?.data?.fortnight, [Validators.required]],
      [DATE_FIELD]: [this.udeuTransferCalendar?.data?.dates, [Validators.required]],
      [EU_DATE_FIELD]: [this.udeuTransferCalendar?.data?.datesEU, [Validators.required]],
      [ACTIVE_FIELD]: [this.udeuTransferCalendar?.data?.active, [Validators.required]],
    });

    this.recipientsFormGroup = this._formBuilder.group({
      [RECIPIENTS_FIELD]: this._formBuilder.array([], ArrayValidators.required)
    });

    this.mainFormGroup = this._formBuilder.group({
      data: this.dataFormGroup,
      recipients: this.recipientsFormGroup,
    });

    if(this.data?.transferActionType === TransferActionTypes.ViewCalendarTransfer) {
      this.mainFormGroup.disable();
    };
  }

  createSteps() {
    this.steps = {
      [MAIN_DATA_STEP]: {
        name: "DEUSTO.NEW_MENU.MENU_ITEMS.TRANSFERS.STEPS.MAIN_DATA.TITLE",
        type: StepTypes.GENERIC,
        form: {
          group: this.dataFormGroup,
          fields: {
            [STRUCTURE_FIELD]: {
              type: MatFormFieldType.SELECT, label: "DEUSTO.NEW_MENU.MENU_ITEMS.TRANSFERS.FIELDS.PROGRAMMING.MAIN_DATA.STRUCTURE"
            },
            [COURSE_FIELD]: {
              type: MatFormFieldType.INPUT, label: "DEUSTO.NEW_MENU.MENU_ITEMS.TRANSFERS.FIELDS.PROGRAMMING.MAIN_DATA.COURSE"
            },
            [MONTH_FIELD]: {
              type: MatFormFieldType.SELECT, label: "DEUSTO.NEW_MENU.MENU_ITEMS.TRANSFERS.FIELDS.PROGRAMMING.MAIN_DATA.MONTH"
            },
            [FORTNIGHT_FIELD]: {
              type: MatFormFieldType.SELECT, label: "DEUSTO.NEW_MENU.MENU_ITEMS.TRANSFERS.FIELDS.PROGRAMMING.MAIN_DATA.FORTNIGHT"
            },
            [DATE_FIELD]: {
              type: MatFormFieldType.INPUT_LANGUAGE, label: "DEUSTO.NEW_MENU.MENU_ITEMS.TRANSFERS.FIELDS.PROGRAMMING.MAIN_DATA.DATE", extraControlName: EU_DATE_FIELD,
                            placeholder: "DEUSTO.NEW_MENU.MENU_ITEMS.TRANSFERS.FIELDS.PROGRAMMING.MAIN_DATA.DATE", extraPlaceholder: "DEUSTO.NEW_MENU.MENU_ITEMS.TRANSFERS.FIELDS.PROGRAMMING.MAIN_DATA.DATE_EU"
            },
            [ACTIVE_FIELD]: {
              type: MatFormFieldType.BOOLEAN, label: "DEUSTO.NEW_MENU.MENU_ITEMS.TRANSFERS.FIELDS.PROGRAMMING.MAIN_DATA.ACTIVE", 
              placeholder: "DEUSTO.NEW_MENU.MENU_ITEMS.TRANSFERS.FIELDS.PROGRAMMING.MAIN_DATA.ACTIVE", extraPlaceholder: "DEUSTO.NEW_MENU.MENU_ITEMS.TRANSFERS.FIELDS.PROGRAMMING.MAIN_DATA.NOT_ACTIVE"
            },
          }
        }
      },
      [RECIPIENTS_STEP]: {
        name: "DEUSTO.NEW_MENU.MENU_ITEMS.TRANSFERS.STEPS.RECIPIENTS.TITLE",
        type: StepTypes.RECIPIENTS,
        form: {
          group: this.recipientsFormGroup,
        }
      }
    }
  }

  createCalendar() {
    // Crear nuevo Calendario Transferencia
    let title = 'DEUSTO.NEW_MENU.MENU_ITEMS.TRANSFERS.ACTIONS.CREATE_CALENDAR.DIALOG.TITLE';
    let message = 'DEUSTO.NEW_MENU.MENU_ITEMS.TRANSFERS.ACTIONS.CREATE_CALENDAR.DIALOG.MESSAGE';
    let noLabel = 'DEUSTO.NEW_MENU.MENU_ITEMS.TRANSFERS.ACTIONS.CREATE_CALENDAR.DIALOG.NO_LABEL';
    let yesLabel = 'DEUSTO.NEW_MENU.MENU_ITEMS.TRANSFERS.ACTIONS.CREATE_CALENDAR.DIALOG.YES_LABEL';

    if(this.data?.transferActionType === TransferActionTypes.EditCalendarTransfer) { 
      // Editar Calendario transferencia
      title = 'DEUSTO.NEW_MENU.MENU_ITEMS.TRANSFERS.ACTIONS.EDIT_CALENDAR.DIALOG.TITLE';
      message = 'DEUSTO.NEW_MENU.MENU_ITEMS.TRANSFERS.ACTIONS.EDIT_CALENDAR.DIALOG.MESSAGE';
      noLabel = 'DEUSTO.NEW_MENU.MENU_ITEMS.TRANSFERS.ACTIONS.EDIT_CALENDAR.DIALOG.NO_LABEL';
      yesLabel = 'DEUSTO.NEW_MENU.MENU_ITEMS.TRANSFERS.ACTIONS.EDIT_CALENDAR.DIALOG.YES_LABEL';
    }

    const dialogRef = this.dialog.open(SMTConfirmDialogComponent, {
      data: { 
        title, 
        message, 
        noLabel, 
        yesLabel 
      },
      minWidth: '250px'
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {      
        const loadingRef = this.dialog.open(SMTLoadingDialogComponent, {
          data: {
            title: 'Cargando...',
          },
          minWidth: '250px'
        });

        const udeuTransferCalendar = new UdeuTransferCalendar();
        udeuTransferCalendar.data = this.createData();
        udeuTransferCalendar.recipients = this.createRecipients();

        const promise = this.data?.transferActionType === TransferActionTypes.EditCalendarTransfer 
        ? this.transferService.updateCalendar(this.data.UUID, udeuTransferCalendar) 
        : this.transferService.createCalendar(udeuTransferCalendar);
        promise.then(() => {
          this.dialogRef.close();
          const route = '/viewer/transfer-calendar';
          this.router.url === route ? this.store.dispatch(new ReloadCustomListTableAction(false)) : this.router.navigate([route]);
        }).catch((err) => {
          console.error("ERROR", err);
          this.store.dispatch(new SnackbarErrorAction('Hubo un error inesperado. Contacte con el administrador.'));
        }).finally(() => {
          loadingRef.close();
        });
      }
    });
  }

  createData(): UdeuData {
    const data = new UdeuData();
    data.structure = (this.dataFormGroup.get(STRUCTURE_FIELD).value as KeyValue<string,string>)?.key;
    data.month = (this.dataFormGroup.get(MONTH_FIELD).value as KeyValue<string,string>)?.key;
    data.fortnight = (this.dataFormGroup.get(FORTNIGHT_FIELD).value as KeyValue<string,string>)?.key;
    data.course = this.dataFormGroup.get(COURSE_FIELD).value;
    data.active = this.dataFormGroup.get(ACTIVE_FIELD).value;
    data.dates = this.dataFormGroup.get(DATE_FIELD).value;
    data.datesEU = this.dataFormGroup.get(EU_DATE_FIELD).value;
    return data;
  }
  
  createRecipients(): UdeuRecipient[] {
    const value = (this.recipientsFormGroup.get(RECIPIENTS_FIELD).getRawValue() as UdeuRecipient[]);
    return value?.length > 0 ? value : [];
  }

  isCompleted(matStep: MatStep) {
    const transferActionType = this.data?.transferActionType ?? TransferActionTypes.CreateCalendarTransfer;
    switch (transferActionType) {
      case TransferActionTypes.CreateCalendarTransfer:
        return !matStep.stepControl.invalid && matStep.interacted;

      case TransferActionTypes.EditCalendarTransfer:
        return !matStep.stepControl.invalid;
    
      default:
        return false;
    }
  }

}
